import { useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { captureException } from '@sentry/react';
import { AutoComplete, Avatar, Col, Row } from 'antd';
import { TbBuildingEstate, TbCircleXFilled } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { userService } from '@/services/database';
import type { CompanyDomainSearchResult } from '@/services/database';
import { AvatarSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';
import { resolveCorporateLogo } from '@/utilities';

const LOADING_OPTION_VALUE = '__company_search_loading__';
const MIN_LENGTH_OPTION_VALUE = '__company_search_min_length__';
const EMPTY_OPTION_VALUE = '__company_search_empty__';
const NON_SELECTABLE_VALUES = [LOADING_OPTION_VALUE, MIN_LENGTH_OPTION_VALUE, EMPTY_OPTION_VALUE];

export interface CompanyDomainSelection {
    domain: string;
    logoUrl?: string;
    name: string;
}

interface CompanyAutocompleteOption extends CompanyDomainSelection {
    disabled?: boolean;
    label: JSX.Element;
    value: string;
}

export interface CompanyDomainAutocompleteProps {
    ariaLabel?: string;
    disabled?: boolean;
    onChange: (value: string) => void;
    onClear?: () => void;
    onSelect?: (company: CompanyDomainSelection) => void;
    placeholder?: string;
    selectedCompany?: CompanyDomainSelection | null;
    selectionValue?: 'domain' | 'name';
    value?: string;
}

const CompanyDomainAutocomplete: FC<CompanyDomainAutocompleteProps> = ({
    ariaLabel = 'Company',
    disabled = false,
    onChange,
    onClear,
    onSelect,
    placeholder = 'Acme Capital...',
    selectedCompany,
    selectionValue = 'name',
    value,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [results, setResults] = useState<CompanyDomainSearchResult[]>([]);
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const requestId = useRef(0);

    useEffect(() => {
        const normalizedQuery = query.trim();

        if (normalizedQuery.length < 2) {
            return;
        }

        const nextRequestId = requestId.current;
        const timeoutId = window.setTimeout(() => {
            void userService
                .searchCompanyDomain(normalizedQuery)
                .then((data) => {
                    if (requestId.current === nextRequestId) {
                        setResults(data ?? []);
                    }
                })
                .catch((error: unknown) => {
                    if (requestId.current === nextRequestId) {
                        setResults([]);
                        captureException(error);
                    }
                })
                .finally(() => {
                    if (requestId.current === nextRequestId) {
                        setLoading(false);
                    }
                });
        }, 400);

        return () => window.clearTimeout(timeoutId);
    }, [query]);

    const handleSearch = (nextQuery: string) => {
        requestId.current += 1;
        setQuery(nextQuery);
        setResults([]);
        setLoading(nextQuery.trim().length >= 2);
    };

    const options = useMemo<CompanyAutocompleteOption[]>(() => {
        if (loading) {
            return [
                {
                    disabled: true,
                    domain: '',
                    label: (
                        <div style={{ padding: '0.25rem 0' }}>
                            {Array.from({ length: 3 }).map((_, index) => (
                                <Row
                                    key={index}
                                    align='middle'
                                    gutter={[8, 0]}
                                    wrap={false}
                                    style={{ padding: '0.375rem 0' }}
                                >
                                    <Col flex='none'>
                                        <AvatarSkeleton active shape='square' size='small' />
                                    </Col>
                                    <Col flex='auto'>
                                        <ButtonSkeleton active block size='small' style={{ height: 14 }} />
                                        <ButtonSkeleton
                                            active
                                            size='small'
                                            style={{ height: 10, marginTop: '0.25rem', width: 128 }}
                                        />
                                    </Col>
                                </Row>
                            ))}
                        </div>
                    ),
                    name: '',
                    value: LOADING_OPTION_VALUE,
                },
            ];
        }

        if (query && query.trim().length < 2) {
            return [
                {
                    disabled: true,
                    domain: '',
                    label: (
                        <div style={{ padding: '0.5rem 0.25rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>
                                Keep typing to search for a company.
                            </small>
                        </div>
                    ),
                    name: '',
                    value: MIN_LENGTH_OPTION_VALUE,
                },
            ];
        }

        if (query.trim().length >= 2 && results.length === 0) {
            return [
                {
                    disabled: true,
                    domain: '',
                    label: (
                        <div style={{ padding: '0.5rem 0.25rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>No companies found.</small>
                        </div>
                    ),
                    name: '',
                    value: EMPTY_OPTION_VALUE,
                },
            ];
        }

        return results.map((company) => ({
            domain: company.domain,
            label: (
                <div>
                    <Row gutter={[6, 0]} wrap={false}>
                        <Col flex='none'>
                            <Avatar
                                icon={<TbBuildingEstate style={{ color: token.colorFillTertiary }} />}
                                shape='square'
                                size={16}
                                src={resolveCorporateLogo(company.domain, company.logo_url)}
                                style={{ border: `1px solid ${token.colorBorder}`, verticalAlign: '-0.125rem' }}
                            />
                        </Col>
                        <Col flex='auto'>{company.name}</Col>
                    </Row>
                    <small style={{ color: token.colorTextTertiary }}>{company.domain}</small>
                </div>
            ),
            logoUrl: company.logo_url,
            name: company.name,
            value: selectionValue === 'domain' ? company.domain : company.name,
        }));
    }, [loading, query, results, selectionValue, token]);

    const fallbackDomain = value?.includes('.') ? value : undefined;
    const avatarSource =
        resolveCorporateLogo(selectedCompany?.domain, selectedCompany?.logoUrl) ?? resolveCorporateLogo(fallbackDomain);

    const clearSelection = () => {
        requestId.current += 1;
        setLoading(false);
        setQuery('');
        setResults([]);
        onChange('');
        onClear?.();
    };

    return (
        <Row gutter={[8, 0]} wrap={false}>
            <Col flex='none'>
                <Avatar
                    icon={<TbBuildingEstate style={{ color: token.colorTextTertiary }} />}
                    shape='square'
                    src={avatarSource}
                    style={{ border: `1px solid ${token.colorBorder}` }}
                />
            </Col>
            <Col flex='auto'>
                <AutoComplete<string, CompanyAutocompleteOption>
                    allowClear={{ clearIcon: <TbCircleXFilled /> }}
                    aria-label={ariaLabel}
                    disabled={disabled}
                    options={options}
                    placeholder={placeholder}
                    value={value}
                    popupRender={(menu) => (
                        <div
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: token.borderRadius * 1.5,
                            }}
                        >
                            {menu}
                        </div>
                    )}
                    onChange={(nextValue) => onChange(nextValue)}
                    onClear={clearSelection}
                    onSearch={handleSearch}
                    onSelect={(selectedValue, option) => {
                        if (NON_SELECTABLE_VALUES.includes(selectedValue)) {
                            return;
                        }

                        requestId.current += 1;
                        setLoading(false);
                        setQuery('');
                        setResults([]);
                        onSelect?.({ domain: option.domain, logoUrl: option.logoUrl, name: option.name });
                    }}
                />
            </Col>
        </Row>
    );
};

export default CompanyDomainAutocomplete;
